/**
 * HabitHeatmap
 * GitHub-style calendar grid showing habit completion for the past N weeks.
 *
 * Props:
 *   dailySelections   – { [dateKey]: { nutrition, movement, mindset } }
 *   revivedDays       – { [dateKey]: { nutrition, movement, mindset } }  (optional)
 *   weeks             – number of weeks to show (default 26 = ~6 months)
 */
export default function HabitHeatmap({
  dailySelections = {},
  revivedDays = {},
  weeks = 26,
}) {
  // ── build the grid ──────────────────────────────────────────────────────────
  // Always end on today, start `weeks` Mon–Sun blocks back
  const today = new Date();
  today.setHours(12, 0, 0, 0);

  // Find the most recent Sunday (end of grid)
  const endDate = new Date(today);
  const endDow = endDate.getDay(); // 0=Sun
  endDate.setDate(endDate.getDate() + (6 - endDow)); // advance to Saturday

  // Start date: go back `weeks` weeks from the Monday before endDate
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - weeks * 7 + 1);
  // Snap to Monday
  const startDow = startDate.getDay();
  const snapBack = startDow === 0 ? 6 : startDow - 1;
  startDate.setDate(startDate.getDate() - snapBack);

  function dateKey(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  // Build columns (each column = one week Mon–Sun)
  const columns = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    const col = [];
    for (let d = 0; d < 7; d++) {
      col.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    columns.push(col);
  }

  // ── score each day ──────────────────────────────────────────────────────────
  function scoreDay(dk) {
    const sel = dailySelections[dk] || {};
    const rev = revivedDays[dk] || {};
    const n = Boolean(sel.nutrition) || Boolean(rev.nutrition);
    const m = Boolean(sel.movement)  || Boolean(rev.movement);
    const s = Boolean(sel.mindset)   || Boolean(rev.mindset);
    const count = (n ? 1 : 0) + (m ? 1 : 0) + (s ? 1 : 0);
    // 0 = none, 1 = partial (1-2), 2 = full (3)
    if (count === 3) return 2;
    if (count > 0) return 1;
    return 0;
  }

  const COLORS = {
    future:  "#f0f4f8",
    none:    "#dbeafe",
    partial: "#93c5fd",
    full:    "#2563eb",
  };

  const LABELS = ["M", "", "W", "", "F", "", "S"];

  // Month labels: find first day of each month in the grid
  const monthLabels = [];
  const seenMonths = new Set();
  columns.forEach((col, ci) => {
    const firstDay = col[0];
    const monthKey = `${firstDay.getFullYear()}-${firstDay.getMonth()}`;
    if (!seenMonths.has(monthKey)) {
      seenMonths.add(monthKey);
      monthLabels.push({ ci, label: firstDay.toLocaleString("default", { month: "short" }) });
    }
  });

  const todayKey = dateKey(today);

  return (
    <div>
      {/* Month labels */}
      <div style={{ display: "flex", marginBottom: "4px", paddingLeft: "18px" }}>
        {columns.map((_, ci) => {
          const ml = monthLabels.find((m) => m.ci === ci);
          return (
            <div key={ci} style={{ flex: 1, minWidth: 0 }}>
              {ml ? (
                <span style={{ fontSize: "9px", color: "#6b8cb0", fontWeight: "bold", whiteSpace: "nowrap" }}>
                  {ml.label}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Grid */}
      <div style={{ display: "flex", gap: "2px" }}>
        {/* Day-of-week labels */}
        <div style={{ display: "flex", flexDirection: "column", gap: "2px", marginRight: "2px" }}>
          {LABELS.map((l, i) => (
            <div key={i} style={{
              width: "14px", height: "14px",
              fontSize: "8px", color: "#6b8cb0",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              {l}
            </div>
          ))}
        </div>

        {/* Week columns */}
        {columns.map((col, ci) => (
          <div key={ci} style={{ display: "flex", flexDirection: "column", gap: "2px", flex: 1, minWidth: 0, maxWidth: "28px" }}>
            {col.map((day) => {
              const dk = dateKey(day);
              const isFuture = dk > todayKey;
              const score = isFuture ? -1 : scoreDay(dk);
              const isToday = dk === todayKey;

              let bg = COLORS.none;
              if (isFuture) bg = COLORS.future;
              else if (score === 2) bg = COLORS.full;
              else if (score === 1) bg = COLORS.partial;

              const sel = dailySelections[dk] || {};
              const rev = revivedDays[dk] || {};
              const tooltip = isFuture ? "" : [
                sel.nutrition || rev.nutrition ? "🍓" : "",
                sel.movement  || rev.movement  ? "🚶‍♀️" : "",
                sel.mindset   || rev.mindset   ? "🌙" : "",
              ].filter(Boolean).join(" ") || "No habits logged";

              return (
                <div
                  key={dk}
                  title={`${dk}${tooltip ? " · " + tooltip : ""}`}
                  style={{
                    width: "100%",
                    aspectRatio: "1/1",
                    borderRadius: "3px",
                    background: bg,
                    border: isToday ? "2px solid #1d4ed8" : "none",
                    boxSizing: "border-box",
                    cursor: "default",
                    flexShrink: 0,
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px", justifyContent: "flex-end" }}>
        <span style={{ fontSize: "10px", color: "#6b8cb0" }}>Less</span>
        {[COLORS.none, COLORS.partial, COLORS.full].map((c) => (
          <div key={c} style={{ width: "12px", height: "12px", borderRadius: "3px", background: c }} />
        ))}
        <span style={{ fontSize: "10px", color: "#6b8cb0" }}>More</span>
      </div>

      {/* Category legend */}
      <div style={{ display: "flex", gap: "12px", marginTop: "6px", flexWrap: "wrap" }}>
        {[
          { color: COLORS.partial, label: "Partial (1–2 habits)" },
          { color: COLORS.full,    label: "Full quest (all 3)" },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <div style={{ width: "10px", height: "10px", borderRadius: "2px", background: color, flexShrink: 0 }} />
            <span style={{ fontSize: "10px", color: "#6b8cb0" }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
